import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, ChevronDown, ChevronUp } from 'lucide-react';
import { useCookieConsent } from '../../hooks/useCookieConsent';

export function CookieConsentBanner() {
  const { showBanner, acceptAll, rejectOptional, savePreferences } = useCookieConsent();
  const [showPrefs, setShowPrefs] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [advertising, setAdvertising] = useState(true);

  if (!showBanner) return null;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        animation: 'slideUp 0.4s ease-out',
      }}
    >
      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
      <div
        style={{
          background: 'rgba(17,17,17,0.97)',
          backdropFilter: 'blur(12px)',
          borderTop: '1px solid rgba(255,255,255,0.1)',
          padding: '16px 20px',
          maxWidth: 720,
          margin: '0 auto 16px',
          borderRadius: 16,
          boxShadow: '0 -4px 30px rgba(0,0,0,0.4)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <Cookie size={20} style={{ color: '#B8860B', flexShrink: 0, marginTop: 2 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 13, fontWeight: 600, color: '#fff',
              lineHeight: 1.5, margin: '0 0 4px',
            }}>
              We use cookies to improve your experience
            </p>
            <p style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 11, color: 'rgba(255,255,255,0.55)',
              lineHeight: 1.5, margin: 0,
            }}>
              Essential cookies keep the site working. Analytics and advertising cookies are optional.{' '}
              <Link to="/cookies" style={{ color: '#B8860B', textDecoration: 'underline' }}>
                Cookie Policy
              </Link>
            </p>
          </div>
        </div>

        {/* Preferences panel */}
        {showPrefs && (
          <div style={{
            marginTop: 12, padding: '12px 0',
            borderTop: '1px solid rgba(255,255,255,0.1)',
          }}>
            {[
              { id: 'essential', label: 'Essential', desc: 'Authentication, security, preferences', locked: true, checked: true },
              { id: 'analytics', label: 'Analytics', desc: 'Page views, feature usage (anonymized)', locked: false, checked: analytics, toggle: () => setAnalytics(v => !v) },
              { id: 'advertising', label: 'Advertising', desc: 'Personalized ads via Google AdSense', locked: false, checked: advertising, toggle: () => setAdvertising(v => !v) },
            ].map(cat => (
              <label
                key={cat.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '6px 0', cursor: cat.locked ? 'default' : 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={cat.checked}
                  disabled={cat.locked}
                  onChange={cat.toggle}
                  style={{ accentColor: '#B8860B', width: 16, height: 16 }}
                />
                <div style={{ flex: 1 }}>
                  <span style={{
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontSize: 12, fontWeight: 600, color: '#fff',
                  }}>
                    {cat.label}
                  </span>
                  {cat.locked && (
                    <span style={{
                      fontSize: 9, fontWeight: 700, color: '#4ade80',
                      marginLeft: 6, textTransform: 'uppercase', letterSpacing: '0.06em',
                    }}>
                      Always on
                    </span>
                  )}
                  <p style={{
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontSize: 10, color: 'rgba(255,255,255,0.4)',
                    margin: '2px 0 0',
                  }}>
                    {cat.desc}
                  </p>
                </div>
              </label>
            ))}
          </div>
        )}

        {/* Actions */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          marginTop: 12, flexWrap: 'wrap',
        }}>
          <button
            onClick={acceptAll}
            style={{
              background: '#B8860B', color: '#fff', border: 'none',
              borderRadius: 8, padding: '8px 16px', cursor: 'pointer',
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 12, fontWeight: 700, letterSpacing: '0.02em',
            }}
          >
            Accept All
          </button>
          <button
            onClick={rejectOptional}
            style={{
              background: 'transparent', color: 'rgba(255,255,255,0.6)',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: 8, padding: '8px 16px', cursor: 'pointer',
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 12, fontWeight: 600,
            }}
          >
            Reject Optional
          </button>
          {showPrefs ? (
            <button
              onClick={() => savePreferences({ analytics, advertising })}
              style={{
                background: 'transparent', color: '#B8860B',
                border: '1px solid #B8860B',
                borderRadius: 8, padding: '8px 16px', cursor: 'pointer',
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: 12, fontWeight: 700,
              }}
            >
              Save Preferences
            </button>
          ) : null}
          <button
            onClick={() => setShowPrefs(v => !v)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'rgba(255,255,255,0.4)', display: 'flex', alignItems: 'center', gap: 4,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 11, fontWeight: 600, padding: '4px 0', marginLeft: 'auto',
            }}
          >
            {showPrefs ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
            {showPrefs ? 'Hide options' : 'Manage'}
          </button>
        </div>
      </div>
    </div>
  );
}
